import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { X } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

interface TrialExpiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  brandsCount?: number;
  personasCount?: number;
  accountsCount?: number;
  draftsCount?: number;
}

export const TrialExpiredModal: React.FC<TrialExpiredModalProps> = ({ isOpen, onClose, onUpgrade, brandsCount = 0, personasCount = 0, accountsCount = 0, draftsCount = 0 }) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  if (!isOpen) return null;

  const getWord = (singularKey: string, pluralKey: string, count: number): string => {
    const isSingular = count <= 1; // Treat 0 like 1 per product requirement
    return t(isSingular ? singularKey : pluralKey);
  };

  const brandsWord = getWord('trialExpired.words.brand_singular', 'trialExpired.words.brand_plural', brandsCount);
  const personasWord = getWord('trialExpired.words.persona_singular', 'trialExpired.words.persona_plural', personasCount);
  const accountsWord = getWord('trialExpired.words.account_singular', 'trialExpired.words.account_plural', accountsCount);
  const draftsWord = getWord('trialExpired.words.draft_singular', 'trialExpired.words.draft_plural', draftsCount);

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black bg-opacity-60" onClick={onClose} />
      <div className="relative z-[71] min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl border-[3px] border-black shadow-2xl w-full max-w-2xl p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="mb-6">
            <h2 className="text-3xl font-extrabold text-gray-900">{t('trialExpired.title', 'Your trial has expired…')}</h2>
            {user?.name || user?.firstName ? (
              <p className="text-gray-900 text-sm mt-1">
                {t('trialExpired.greeting', 'Hi {{name}},', { name: user?.firstName || user?.name })}
              </p>
            ) : null}
            <p className="text-gray-800 text-sm mt-2">
              <Trans
                i18nKey="trialExpired.progress"
                values={{ brands: brandsCount, personas: personasCount, accounts: accountsCount, drafts: draftsCount, brandsWord, personasWord, accountsWord, draftsWord }}
                components={{ b: <strong />, i: <em /> }}
              />
            </p>
            <p className="text-gray-700 mt-3">
              <Trans i18nKey="trialExpired.description">
                To continue enjoying your <strong className="font-semibold text-black">own AI Social Media assistant</strong>, you'll need to upgrade to Solo, Team or Agency Pro
              </Trans>
            </p>
          </div>

          <ul className="space-y-2 mb-6">
            <li className="flex items-start gap-2">
              <span className="text-sm leading-tight">🚀</span>
              <p className="text-black text-sm leading-tight">{t('trialExpired.features.unlimitedPosts', 'Publish unlimited posts across all platforms')}</p>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sm leading-tight">🎨</span>
              <p className="text-black text-sm leading-tight">{t('trialExpired.features.unlimitedAiContent', 'Generate unlimited AI content (posts, captions, images)')}</p>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sm leading-tight">📊</span>
              <p className="text-black text-sm leading-tight">{t('trialExpired.features.analytics', 'Access advanced analytics & AI-powered insights')}</p>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sm leading-tight">🧠</span>
              <p className="text-black text-sm leading-tight">{t('trialExpired.features.algorithms', 'Unlock our proprietary AI algorithm suite')}</p>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sm leading-tight">⚡</span>
              <p className="text-black text-sm leading-tight">{t('trialExpired.features.support', 'Priority support & faster processing')}</p>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sm leading-tight">✅</span>
              <p className="text-black text-sm leading-tight">{t('trialExpired.features.more', 'And much more!')}</p>
            </li>
          </ul>

          <div className="flex justify-end">
            <button
              onClick={onUpgrade}
              className="inline-flex items-center px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              {t('trialExpired.upgradeCta', 'Upgrade')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


